import type { DocumentTree } from '../dto/proposal-document.dto.js'
import { SEASONS, type Season } from '../enums/season.enum.js'
import { seasonsFrom } from './seasons.js'

/*
 * Splitting a site's tree work by season (docs/seasonal-work.md).
 *
 * Shared so the survey screen can say what a bid comes to while the
 * technician is still editing it, by the same rule the proposal is built
 * with. Two copies would be two answers to which visit a treatment is in.
 */

type DocumentProblem = DocumentTree['problems'][number]
type DocumentSolution = DocumentProblem['solutions'][number]

/** A treatment as a document is built from it: the document's, with its season. */
type SourceSolution = DocumentSolution & {
  /** When it is done. Absent or null: no season was chosen. */
  season?: Season | null
}

/** A problem as a document is built from it, with its season and its treatments'. */
export type SourceProblem = Omit<DocumentProblem, 'solutions'> & {
  /**
   * When it is dealt with. Absent or null: no season was chosen, and it goes
   * with the tree's pruning.
   */
  season?: Season | null
  solutions: SourceSolution[]
}

/**
 * A tree as a document is built from it: everything the document shows, plus
 * when each part of its work is done (docs/seasonal-work.md). The seasons are
 * read here and never reach the document, which shows the next visit's work
 * in its ordinary blocks and the rest under Future work.
 */
export type DocumentSourceTree = Omit<DocumentTree, 'problems'> & {
  /**
   * When its pruning, its estimate and a quoted removal are done. Absent or
   * null: no season was chosen.
   */
  pruningSeason?: Season | null
  problems: SourceProblem[]
}

/** One site's work, split by when it is done. */
export interface SeasonalSplit {
  /** The season of the next visit, or null when no work here has a season. */
  nextSeason: Season | null
  /**
   * The next visit's work, tree by tree, as every existing block reads it:
   * the map, schedule, problems, price, plans and acceptance.
   */
  next: DocumentTree[]
  /** Work for later seasons, in the order the year brings them. */
  later: { season: Season; trees: DocumentTree[] }[]
}

/**
 * Whether a tree has pruning to do: jobs, a real estimate, a pruning price,
 * or a removal quote.
 *
 * Zero minutes is not an estimate. "No pruning recommended" stores a zero on
 * purpose, and that zero is the answer rather than work, so only a removal
 * counts on one. A bare zero, with no mark and no other quote, is not a
 * decision yet.
 */
function hasPruning(tree: DocumentSourceTree): boolean {
  const prunes =
    tree.noPruningRecommended !== true &&
    (tree.jobs.length > 0 ||
      (tree.estimatedLaborMinutes ?? 0) > 0 ||
      (tree.pruningPriceOverride ?? null) !== null)
  return prunes || (tree.removal ?? null) !== null
}

/** Whether a problem's price was set by hand rather than by its treatments. */
function isPricedByHand(problem: SourceProblem): boolean {
  return (problem.priceOverride ?? null) !== null
}

/**
 * When all of a problem priced by hand is done. One figure cannot be split
 * between visits, so its treatments go together, in the first one's season;
 * one with no treatment goes where any such problem does, in its own season
 * or the pruning's.
 */
function handPricedSeason(
  problem: SourceProblem,
  pruningSeason: Season | null,
): Season | null {
  const [first] = problem.solutions
  return first === undefined
    ? (problem.season ?? pruningSeason)
    : treatmentSeason(first, problem)
}

/**
 * When a treatment is done: its own season, or its problem's when it has
 * none. Null when neither was chosen, which is the next visit.
 */
function treatmentSeason(
  solution: SourceSolution,
  problem: SourceProblem,
): Season | null {
  return solution.season ?? problem.season ?? null
}

/**
 * Whether the pruning season says anything: a tree with pruning to do, or a
 * problem with no treatment and no season of its own, which goes with the
 * pruning. A season tapped on a tree with neither decides nothing.
 */
function pruningIsWork(tree: DocumentSourceTree): boolean {
  return (
    hasPruning(tree) ||
    tree.problems.some(
      (problem) =>
        problem.solutions.length === 0 && (problem.season ?? null) === null,
    )
  )
}

/** The seasons this tree's work is in, leaving out work with none. */
function seasonsOf(tree: DocumentSourceTree): Season[] {
  const seasons: Season[] = []
  const pruning = tree.pruningSeason ?? null
  if (pruning !== null && pruningIsWork(tree)) seasons.push(pruning)
  for (const problem of tree.problems) {
    if (isPricedByHand(problem) && problem.solutions.length > 0) {
      const season = handPricedSeason(problem, pruning)
      if (season !== null) seasons.push(season)
      continue
    }
    if (problem.solutions.length === 0) {
      const own = problem.season ?? null
      if (own !== null) seasons.push(own)
      continue
    }
    for (const solution of problem.solutions) {
      const season = treatmentSeason(solution, problem)
      if (season !== null) seasons.push(season)
    }
  }
  return seasons
}

/**
 * Whether work in `season` is done in the visit being built. Work with no
 * season goes in the next visit, which is where all work went before
 * seasons, so nothing recorded earlier moves.
 */
function belongs(
  season: Season | null,
  visit: Season | null,
  isNext: boolean,
): boolean {
  return season === null ? isNext : season === visit
}

/** A treatment as the document shows it, without its season. */
function toDocumentSolution(solution: SourceSolution): DocumentSolution {
  return {
    label: solution.label,
    description: solution.description,
    minutes: solution.minutes,
    amount: solution.amount,
  }
}

/**
 * The part of a tree's work done on one visit, as a document tree, and
 * whether there is any.
 *
 * Built fresh rather than spread, because the document's objects are strict:
 * a season left on one would make a frozen proposal fail to parse.
 */
function partOf(
  tree: DocumentSourceTree,
  visit: Season | null,
  isNext: boolean,
): { tree: DocumentTree; hasWork: boolean } {
  const { pruningSeason, problems, ...rest } = tree
  const pruning = belongs(pruningSeason ?? null, visit, isNext)

  const kept: DocumentProblem[] = []
  for (const problem of problems) {
    // The problem's season is taken off with the treatments', for the same
    // reason: the document's objects are strict.
    const { solutions, season, ...finding } = problem
    /*
     * A problem with no treatment goes in its own season, or with the pruning
     * when it has none, as every problem recorded before problems had seasons
     * does: the pruning is usually what answers those (deadwood, crossing
     * limbs).
     */
    if (solutions.length === 0) {
      if (belongs(season ?? pruningSeason ?? null, visit, isNext)) {
        kept.push({ ...finding, solutions: [] })
      }
      continue
    }
    if (isPricedByHand(problem)) {
      if (belongs(handPricedSeason(problem, pruningSeason ?? null), visit, isNext)) {
        kept.push({ ...finding, solutions: solutions.map(toDocumentSolution) })
      }
      continue
    }
    const here = solutions.filter((solution) =>
      belongs(treatmentSeason(solution, problem), visit, isNext),
    )
    if (here.length > 0) {
      kept.push({ ...finding, solutions: here.map(toDocumentSolution) })
    }
  }

  const prunesHere = pruning && hasPruning(tree)
  return {
    tree: {
      ...rest,
      jobs: prunesHere ? rest.jobs : [],
      // Zero, not null: on a visit that does not prune the tree its pruning
      // time is none, where null would read as "not estimated yet" and stop
      // the proposal being sent (`describeUnsendable`).
      estimatedLaborMinutes: prunesHere ? rest.estimatedLaborMinutes : 0,
      pruningPriceOverride: prunesHere
        ? (rest.pruningPriceOverride ?? null)
        : null,
      removal: prunesHere ? (rest.removal ?? null) : null,
      problems: kept,
    },
    hasWork: prunesHere || kept.length > 0,
  }
}

/**
 * Split one site's trees into the next visit and the seasons after it.
 *
 * The next visit is the first season with work, counting from the season the
 * site's visit was in (docs/seasonal-work.md, "Seasons count from the
 * visit"). `visitSeason` is null only for a site with no visit, which has no
 * findings to split; then the year simply starts from winter.
 *
 * A tree with no work at all stays in the next visit, as every surveyed tree
 * was shown before seasons existed.
 */
export function splitBySeason(
  trees: readonly DocumentSourceTree[],
  visitSeason: Season | null,
  /**
   * The one season a rebid bids (`Proposal.season`). Its work is the next
   * visit and everything else is left out: work in other seasons, and work
   * with none, which went in the visit the earlier proposal priced. Trees
   * with no work that season are left out too, since a rebid is that
   * season's list and nothing more.
   */
  onlySeason: Season | null = null,
): SeasonalSplit {
  if (onlySeason !== null) {
    const next = trees.flatMap((tree) => {
      const part = partOf(tree, onlySeason, false)
      return part.hasWork ? [part.tree] : []
    })
    return {
      nextSeason: next.length === 0 ? null : onlySeason,
      next,
      later: [],
    }
  }
  const present = new Set(trees.flatMap(seasonsOf))
  const order = seasonsFrom(visitSeason ?? SEASONS[0])
  const nextSeason = order.find((season) => present.has(season)) ?? null

  /*
   * A tree marked "No pruning recommended" is on the next visit's schedule
   * even when all its other work is later: the arborist's answer about it is
   * part of what this survey found, and the client should read it.
   */
  const next = trees.flatMap((tree) => {
    const part = partOf(tree, nextSeason, true)
    return part.hasWork ||
      seasonsOf(tree).length === 0 ||
      tree.noPruningRecommended === true
      ? [part.tree]
      : []
  })

  const later = order
    .slice(nextSeason === null ? order.length : order.indexOf(nextSeason) + 1)
    .filter((season) => present.has(season))
    .map((season) => ({
      season,
      trees: trees.flatMap((tree) => {
        const part = partOf(tree, season, false)
        return part.hasWork ? [part.tree] : []
      }),
    }))

  return { nextSeason, next, later }
}
